/**
 * Packaging OS — locale state hook (Phase 1R-A-Fix-2).
 *
 * Module-level subscriber state so every consumer in the tree (header,
 * page, switcher, route-prefix sync wrapper) sees the same locale value
 * and stays in sync without a Context provider.
 *
 * Source of truth on first mount: the `packaging_os_locale` cookie, with
 * `DEFAULT_LOCALE` as the fallback. Cookie + <html lang dir> are written
 * eagerly on every change so the browser tab is correctly attributed for
 * RTL/LTR direction at the same time as React renders.
 */

import { useEffect, useState } from "react";
import { DEFAULT_LOCALE, dirFor, findLocale, type LocaleCode } from "./locales";

const COOKIE_NAME = "packaging_os_locale";
const COOKIE_MAX_AGE_DAYS = 365;

function readCookieLocale(): LocaleCode {
  if (typeof document === "undefined") return DEFAULT_LOCALE;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return DEFAULT_LOCALE;
  const raw = match.split("=")[1];
  const found = findLocale(raw);
  return found ? found.code : DEFAULT_LOCALE;
}

function writeCookieLocale(code: LocaleCode): void {
  if (typeof document === "undefined") return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  // SameSite=Lax is sufficient for a non-secret display preference.
  document.cookie = `${COOKIE_NAME}=${code}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
}

function applyHtmlAttrs(code: LocaleCode): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const locale = findLocale(code);
  if (!locale) return;
  root.setAttribute("lang", locale.lang);
  root.setAttribute("dir", locale.dir);
}

// ---------------------------------------------------------------------------
// Module-level state. Lazily initialized so multiple test files each pick up
// a fresh value from the (cleared) cookie. Subscribers receive every change.
// ---------------------------------------------------------------------------

const subscribers = new Set<(next: LocaleCode) => void>();
let currentLocale: LocaleCode | null = null;

function ensureInitialized(): LocaleCode {
  if (currentLocale === null) {
    currentLocale = readCookieLocale();
    applyHtmlAttrs(currentLocale);
  }
  return currentLocale;
}

function broadcast(next: LocaleCode): void {
  if (next === currentLocale) return;
  currentLocale = next;
  writeCookieLocale(next);
  applyHtmlAttrs(next);
  subscribers.forEach((sub) => sub(next));
}

/** Test helper — resets the module-level cache so DEFAULT_LOCALE is re-read
 * from the (cleared) cookie at the next consumer mount. */
export function __resetLocaleForTests(): void {
  currentLocale = null;
  subscribers.clear();
}

export function useLocale(): [LocaleCode, (next: LocaleCode) => void] {
  const [locale, setLocaleState] = useState<LocaleCode>(() =>
    ensureInitialized(),
  );

  useEffect(() => {
    const sub = (next: LocaleCode): void => setLocaleState(next);
    subscribers.add(sub);
    // Catch a value that changed between initial render and effect attach.
    if (currentLocale !== null && currentLocale !== locale) {
      setLocaleState(currentLocale);
    }
    return () => {
      subscribers.delete(sub);
    };
  }, [locale]);

  const setLocale = (next: LocaleCode): void => {
    broadcast(next);
  };

  return [locale, setLocale];
}

/** Re-export so callers don't need to import locales.ts directly for UI bits. */
export { dirFor };
